/**
 * App Initializer für POS Terminal
 *
 * Läuft VOR Router-Navigation und stellt sicher, dass:
 * 1. Firebase Auth vollständig initialisiert ist
 * 2. Custom Claims geladen sind
 * 3. TenantContext gesetzt ist
 * 4. Guards danach mit vollständigem State arbeiten können
 *
 * @author TypeScript Developer
 * @version 1.0.0
 */

import { inject } from '@angular/core';
import { Auth, getIdTokenResult } from '@angular/fire/auth';
import { TenantContextService } from '@pos-shared/services';
import { getEffectiveTenantId, CrmCustomClaims } from '@pos-shared/models';

/**
 * Mappt Custom Claims Role zu TenantContext Role
 */
function mapRoleToTenantRole(role: string | null): 'owner' | 'admin' | 'manager' | 'cashier' | 'viewer' | null {
  if (!role) return null;

  const roleMap: Record<string, 'owner' | 'admin' | 'manager' | 'cashier' | 'viewer'> = {
    'pos_admin': 'admin',
    'pos_manager': 'manager',
    'pos_cashier': 'cashier',
    'pos_seller': 'cashier'
  };

  return roleMap[role] || 'cashier';
}

/**
 * Ermittelt Permissions basierend auf Role
 */
function getRolePermissions(role: string): string[] {
  const permissions: Record<string, string[]> = {
    'pos_admin': ['read', 'write', 'delete', 'manage_users'],
    'pos_manager': ['read', 'write', 'delete'],
    'pos_cashier': ['read', 'write'],
    'pos_seller': ['read', 'write'],
    'viewer': ['read']
  };

  return permissions[role] || permissions['viewer'];
}

/**
 * Initialisiert App-State vor Router-Navigation
 */
export function initializeApp() {
  const auth = inject(Auth);
  const tenantContext = inject(TenantContextService);

  return () => new Promise<void>(async (resolve) => {
    console.log('[Terminal AppInitializer] Warte auf Firebase Auth...');

    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      unsubscribe();

      if (user) {
        console.log('[Terminal AppInitializer] User ist eingeloggt:', user.email);

        try {
          const idTokenResult = await getIdTokenResult(user);
          const claims = idTokenResult.claims as CrmCustomClaims;

          const effectiveTenantId = getEffectiveTenantId(claims);

          if (effectiveTenantId) {
            const effectiveRole = (claims as any).role;
            const mappedRole = mapRoleToTenantRole(effectiveRole);
            const permissions = getRolePermissions(effectiveRole || 'viewer');

            const primaryLocationId = (claims as any).primaryLocationId || null;
            const allowedLocationIds = (claims as any).allowedLocationIds || [];

            // Zuerst localStorage laden, damit bestehende Location nicht verloren geht
            tenantContext.loadFromStorage();
            const existingLocationId = tenantContext.locationId();

            const contextUpdate: any = {
              tenantId: effectiveTenantId,
              tenantName: null,
              role: mappedRole,
              permissions: permissions,
              locationIds: allowedLocationIds
            };

            // Location nur überschreiben wenn Claims eine primaryLocationId haben
            if (primaryLocationId) {
              contextUpdate.locationId = primaryLocationId;
              contextUpdate.locationName = null;
            }
            // Sonst: existingLocationId aus localStorage bleibt erhalten

            tenantContext.setContext(contextUpdate);

            console.log('[Terminal AppInitializer] TenantContext gesetzt:', {
              tenantId: effectiveTenantId,
              role: mappedRole,
              locationId: primaryLocationId || existingLocationId || 'keine'
            });
          } else {
            tenantContext.loadFromStorage();
            console.log('[Terminal AppInitializer] Kein Tenant - localStorage geladen');
          }
        } catch (error) {
          console.error('[Terminal AppInitializer] Fehler beim Laden der Claims:', error);
          tenantContext.loadFromStorage();
        }
      } else {
        console.log('[Terminal AppInitializer] Kein User eingeloggt');
        tenantContext.loadFromStorage();
      }

      console.log('[Terminal AppInitializer] Initialisierung abgeschlossen');
      resolve();
    });
  });
}
